import { useState, useEffect, useMemo } from "react";
import { base44 } from "@/api/base44Client";
import { toast } from "sonner";

const T = {
  ar: {
    title: "اختر الأصناف (اختياري)",
    hint: "حدد ما تريد غسله لمعرفة التكلفة التقديرية. السعر النهائي يُعتمد عند الفرز.",
    washIron: "غسيل+كوي",
    washOnly: "غسيل",
    ironOnly: "كوي",
    currency: "ر.س",
    itemsWord: "صنف",
    cartTitle: "🧺 سلة الطلب",
    cartEmpty: "لم تختر أي أصناف بعد",
    subtotal: "المجموع الفرعي",
    couponLabel: "كود الخصم (اختياري)",
    couponPlaceholder: "أدخل كود الخصم",
    couponApply: "تطبيق",
    couponChecking: "...",
    couponValid: "✅ كوبون صالح",
    couponInvalid: "❌ كود غير صالح",
    couponExpired: "❌ الكود منتهي الصلاحية",
    discount: "الخصم",
    total: "الإجمالي المستحق",
    couponDetails: "تفاصيل الكوبون",
    validUntil: "صالح حتى",
    minInvoice: "الحد الأدنى للفاتورة",
    termsLabel: "الشروط",
    pendingNote: "🔒 الكوبون معلّق — لن يُخصم المبلغ الآن. سيؤكّده المندوب ويطبّقه نهائياً عند فرز الأصناف عند الاستلام.",
    previewDiscount: "خصم تقديري (يُطبّق عند الفرز)",
    totalNow: "المبلغ المستحق حالياً",
    totalNote: "الخصم يُطبّق نهائياً عند تأكيد المندوب",
    minNotMet: "السلة أقل من الحد الأدنى للفاتورة — لن يُطبّق الكوبون حتى بلوغ الحد",
    typePercent: "خصم بنسبة",
    typeAmount: "خصم مبلغ",
    typeFreeDelivery: "توصيل مجاني",
    typeFreeService: "خدمة مجانية",
    typeNoLuck: "حظ أوفر",
    clearCoupon: "إزالة الكوبون",
    noServices: "لا توجد خدمات في هذه الفئة",
    noCategoryServices: "لا توجد خدمات متاحة",
    remove: "إزالة",
    otherServices: "خدمات أخرى",
    addedToast: "تمت الإضافة إلى السلة",
    viewCart: "عرض السلة",
    hideCart: "إخفاء",
    emptyCart: "السلة فارغة",
  },
  en: {
    title: "Select Items (Optional)",
    hint: "Choose what you want washed to see an estimated cost. Final price confirmed upon sorting.",
    washIron: "Wash+Iron",
    washOnly: "Wash",
    ironOnly: "Iron",
    currency: "SAR",
    itemsWord: "items",
    cartTitle: "🧺 Order Cart",
    cartEmpty: "No items selected yet",
    subtotal: "Subtotal",
    couponLabel: "Discount Code (Optional)",
    couponPlaceholder: "Enter discount code",
    couponApply: "Apply",
    couponChecking: "...",
    couponValid: "✅ Valid coupon",
    couponInvalid: "❌ Invalid code",
    couponExpired: "❌ Code expired",
    discount: "Discount",
    total: "Total Due",
    couponDetails: "Coupon Details",
    validUntil: "Valid until",
    minInvoice: "Min. invoice",
    termsLabel: "Terms",
    pendingNote: "🔒 Coupon is pending — no deduction now. The delegate will confirm and apply it upon sorting at pickup.",
    previewDiscount: "Estimated discount (applied at sorting)",
    totalNow: "Amount due now",
    totalNote: "Discount applied upon delegate confirmation",
    minNotMet: "Cart is below the minimum invoice — coupon won't apply until reached",
    typePercent: "Percentage off",
    typeAmount: "Amount off",
    typeFreeDelivery: "Free delivery",
    typeFreeService: "Free service",
    typeNoLuck: "Better luck",
    clearCoupon: "Remove coupon",
    noServices: "No services in this category",
    noCategoryServices: "No services available",
    remove: "Remove",
    otherServices: "Other Services",
    addedToast: "Added to cart",
    viewCart: "View Cart",
    hideCart: "Hide",
    emptyCart: "Cart is empty",
  }
};

const TYPE_LABELS = {
  wash_iron: { ar: "غسيل+كوي", en: "Wash+Iron" },
  wash_only: { ar: "غسيل", en: "Wash" },
  iron_only: { ar: "كوي", en: "Iron" },
};

export default function LaundryOrderBuilder({ services, lang = "ar", onChange }) {
  const tr = T[lang] || T.ar;
  const isAr = lang === "ar";
  const [cart, setCart] = useState({});
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [couponMsg, setCouponMsg] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);

  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    base44.entities.LaundryCategory.filter({ is_active: true }).then(cats => {
      const sorted = cats.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setCategories(sorted);
      if (sorted.length > 0 && !activeCat) setActiveCat(sorted[0].id);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeServices = useMemo(
    () => (services || []).filter(s => s.is_active).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [services]
  );

  const catServices = useMemo(() => activeServices.filter(s => s.category_id === activeCat), [activeServices, activeCat]);
  const uncategorized = useMemo(() => activeServices.filter(s => !s.category_id), [activeServices]);

  const getUnitPrice = (s, type) => {
    if (type === "wash_iron") {
      const onSale = s.sale_price && s.sale_start && s.sale_end && today >= s.sale_start && today <= s.sale_end;
      return onSale ? s.sale_price : s.price;
    }
    if (type === "wash_only") return s.price_wash_only;
    if (type === "iron_only") return s.price_iron_only;
    return null;
  };

  const getAvailableTypes = (s) => {
    const types = [];
    if (s.price) types.push("wash_iron");
    if (s.price_wash_only) types.push("wash_only");
    if (s.price_iron_only) types.push("iron_only");
    return types;
  };

  const addToCart = (s, type) => {
    const key = `${s.id}__${type}`;
    const unitPrice = getUnitPrice(s, type);
    if (unitPrice == null) return;
    const name = isAr ? s.name : (s.name_en || s.name);
    const label = isAr ? TYPE_LABELS[type].ar : TYPE_LABELS[type].en;
    setCart(prev => {
      if (prev[key]) {
        return { ...prev, [key]: { ...prev[key], qty: prev[key].qty + 1 } };
      }
      return { ...prev, [key]: { serviceId: s.id, serviceName: name, type, label, unitPrice, qty: 1 } };
    });
    toast.success(`${tr.addedToast}: ${name} (${label})`, { duration: 1500 });
  };

  const updateQty = (key, delta) => {
    setCart(prev => {
      const item = prev[key];
      if (!item) return prev;
      const newQty = item.qty + delta;
      if (newQty <= 0) {
        const rest = { ...prev };
        delete rest[key];
        return rest;
      }
      return { ...prev, [key]: { ...item, qty: newQty } };
    });
  };

  const removeItem = (key) => {
    setCart(prev => { const rest = { ...prev }; delete rest[key]; return rest; });
  };

  const subtotal = useMemo(
    () => Object.values(cart).reduce((sum, item) => sum + item.unitPrice * item.qty, 0),
    [cart]
  );
  const count = useMemo(() => Object.values(cart).reduce((sum, item) => sum + item.qty, 0), [cart]);

  const discount = useMemo(() => {
    if (!couponData?.offer || subtotal <= 0) return 0;
    const offer = couponData.offer;
    if (offer.min_invoice_amount && subtotal < offer.min_invoice_amount) return 0;
    let disc = 0;
    if (offer.offer_type === "discount_percent") {
      disc = subtotal * ((offer.discount_value || 0) / 100);
      if (offer.max_discount_amount && disc > offer.max_discount_amount) disc = offer.max_discount_amount;
    } else if (offer.offer_type === "discount_amount") {
      disc = Math.min(offer.discount_value || 0, subtotal);
    }
    return Math.round(disc * 100) / 100;
  }, [couponData, subtotal]);

  const total = Math.max(0, Math.round((subtotal - discount) * 100) / 100);

  useEffect(() => {
    const servicesArray = Object.values(cart).map(item => ({
      service_name: `${item.serviceName} (${item.label})`,
      quantity: item.qty,
      unit_price: item.unitPrice,
      total: item.unitPrice * item.qty,
    }));
    // الكوبون معلّق: لا يُطبّق الخصم فعلياً عند الطلب — يطبّقه المندوب عند الفرز
    onChange?.({ services: servicesArray, subtotal, discount: 0, total_amount: subtotal, coupon_code: couponData ? couponCode.trim() : null });
  }, [cart, couponData, subtotal, discount, total]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) return;
    setCouponLoading(true);
    setCouponMsg("");
    setCouponData(null);
    try {
      const coupons = await base44.entities.Coupon.filter({ code, status: "unused" });
      if (coupons.length === 0) { setCouponMsg(tr.couponInvalid); return; }
      const coupon = coupons[0];
      if (coupon.expires_at && new Date(coupon.expires_at) < new Date()) { setCouponMsg(tr.couponExpired); return; }
      const offer = await base44.entities.Offer.get(coupon.offer_id);
      setCouponData({ offer, coupon });
      setCouponMsg(tr.couponValid);
    } catch (e) {
      setCouponMsg(tr.couponInvalid);
    } finally {
      setCouponLoading(false);
    }
  };

  const cartItems = Object.entries(cart);

  // Service card renderer
  const renderService = (s) => {
    const types = getAvailableTypes(s);
    if (types.length === 0) return null;
    const name = isAr ? s.name : (s.name_en || s.name);
    return (
      <div key={s.id} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 px-4 py-3 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xl">{s.icon || "👕"}</span>
          <p className="font-bold text-gray-900 dark:text-white text-sm flex-1">{name}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {types.map(type => {
            const price = getUnitPrice(s, type);
            const label = isAr ? TYPE_LABELS[type].ar : TYPE_LABELS[type].en;
            return (
              <button key={type} type="button" onClick={() => addToCart(s, type)}
                className="flex items-center gap-1.5 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800 rounded-xl px-3 py-1.5 text-xs font-bold hover:bg-cyan-100 dark:hover:bg-cyan-900/30 transition-all">
                <span>{label}</span>
                <span className="font-black">{price}</span>
                <span className="text-cyan-500 text-[10px]">{tr.currency}</span>
                <span className="text-cyan-500">＋</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="text-center">
        <h3 className="font-black text-gray-900 dark:text-white text-base">{tr.title}</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tr.hint}</p>
      </div>

      {/* Sticky Cart Summary Bar */}
      <button type="button" onClick={() => setCartOpen(o => !o)}
        className="sticky top-2 z-20 w-full flex items-center justify-between gap-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-2xl px-4 py-3 shadow-lg">
        <span className="flex items-center gap-2 font-bold text-sm">
          <span className="text-lg">🧺</span>
          <span className="bg-white/25 rounded-full px-2 py-0.5 text-xs font-black min-w-[24px] text-center">{count}</span>
          <span className="text-xs opacity-90">{tr.itemsWord}</span>
        </span>
        {count > 0 ? (
          <span className="flex items-center gap-2">
            <span className="font-black text-base">{subtotal.toFixed(2)} {tr.currency}</span>
            <span className="text-xs opacity-90">{cartOpen ? tr.hideCart : tr.viewCart}</span>
          </span>
        ) : (
          <span className="text-xs opacity-80">{tr.emptyCart}</span>
        )}
      </button>

      {/* Cart Panel (expandable) */}
      {cartOpen && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-4 shadow-sm space-y-3">
          <p className="font-black text-gray-700 dark:text-gray-300 text-sm">{tr.cartTitle}</p>
          {cartItems.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-3">{tr.cartEmpty}</p>
          ) : (
            <div className="space-y-2">
              {cartItems.map(([key, item]) => (
                <div key={key} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700/50 rounded-xl px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{item.serviceName}</p>
                    <p className="text-xs text-gray-500">{item.label} · {item.unitPrice} {tr.currency}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => updateQty(key, -1)} className="w-7 h-7 rounded-lg bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 font-bold text-sm">−</button>
                    <span className="w-7 text-center font-black text-sm text-gray-900 dark:text-white">{item.qty}</span>
                    <button type="button" onClick={() => updateQty(key, 1)} className="w-7 h-7 rounded-lg bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 font-bold text-sm">+</button>
                  </div>
                  <div className="text-end">
                    <p className="font-black text-sm text-gray-900 dark:text-white">{(item.unitPrice * item.qty).toFixed(2)}</p>
                    <button type="button" onClick={() => removeItem(key)} className="text-[10px] text-red-500 hover:text-red-600">{tr.remove}</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Coupon */}
          <div className="border-t border-gray-100 dark:border-gray-700 pt-3 space-y-2">
            <label className="text-xs font-bold text-gray-600 dark:text-gray-400 block">{tr.couponLabel}</label>
            <div className="flex gap-2">
              <input type="text" value={couponCode}
                onChange={e => { setCouponCode(e.target.value); setCouponMsg(""); setCouponData(null); }}
                placeholder={tr.couponPlaceholder} dir="ltr"
                className="flex-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan-500 text-gray-900 dark:text-white" />
              <button type="button" onClick={handleApplyCoupon} disabled={couponLoading || !couponCode.trim()}
                className="bg-cyan-600 text-white font-bold text-sm px-4 rounded-xl hover:bg-cyan-700 transition-all disabled:opacity-50">
                {couponLoading ? tr.couponChecking : tr.couponApply}
              </button>
            </div>
            {couponMsg && !couponData && (
              <p className="text-xs font-medium text-red-500">{couponMsg}</p>
            )}

            {/* Coupon Details (preview — not yet applied) */}
            {couponData && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black text-green-700 dark:text-green-400">✅ {tr.couponValid}</span>
                  <button type="button" onClick={() => { setCouponData(null); setCouponMsg(""); setCouponCode(""); }}
                    className="text-xs text-red-500 hover:text-red-600 font-bold">{tr.clearCoupon} ✕</button>
                </div>
                <div>
                  <p className="font-bold text-sm text-gray-900 dark:text-white">{couponData.offer.title}</p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    {(() => {
                      const o = couponData.offer;
                      let label = "";
                      if (o.offer_type === "discount_percent") label = `${tr.typePercent} ${o.discount_value || 0}%`;
                      else if (o.offer_type === "discount_amount") label = `${tr.typeAmount} ${o.discount_value || 0} ${tr.currency}`;
                      else if (o.offer_type === "free_delivery") label = `🚚 ${tr.typeFreeDelivery}`;
                      else if (o.offer_type === "free_service") label = `🎁 ${tr.typeFreeService}`;
                      else if (o.offer_type === "no_luck") label = `🍀 ${tr.typeNoLuck}`;
                      return <span className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs font-bold px-2 py-0.5 rounded-full">{label}</span>;
                    })()}
                  </div>
                </div>
                {couponData.offer.min_invoice_amount > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {tr.minInvoice}: <span className="font-bold text-gray-700 dark:text-gray-300">{couponData.offer.min_invoice_amount} {tr.currency}</span>
                    {subtotal > 0 && subtotal < couponData.offer.min_invoice_amount && (
                      <span className="block text-amber-600 dark:text-amber-400 font-bold mt-0.5">⚠️ {tr.minNotMet}</span>
                    )}
                  </p>
                )}
                {couponData.offer.terms && (
                  <div className="bg-amber-50 dark:bg-amber-900/30 rounded-lg p-2">
                    <p className="text-xs text-amber-800 dark:text-amber-300"><strong>📋 {tr.termsLabel}:</strong> {couponData.offer.terms}</p>
                  </div>
                )}
                {couponData.coupon.expires_at && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{tr.validUntil}: {new Date(couponData.coupon.expires_at).toLocaleDateString(isAr ? "ar-SA" : "en-US")}</p>
                )}
                <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-2 flex items-start gap-1.5">
                  <span className="text-xs">🔒</span>
                  <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">{tr.pendingNote}</p>
                </div>
              </div>
            )}
          </div>

          {/* Summary */}
          {subtotal > 0 && (
            <div className="border-t border-gray-100 dark:border-gray-700 pt-3 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">{tr.subtotal}</span>
                <span className="font-bold text-gray-900 dark:text-white">{subtotal.toFixed(2)} {tr.currency}</span>
              </div>
              {couponData && discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-green-600 dark:text-green-400">{tr.previewDiscount}</span>
                  <span className="font-bold text-green-600 dark:text-green-400">−{discount.toFixed(2)} {tr.currency}</span>
                </div>
              )}
              <div className="flex justify-between pt-1 border-t border-dashed border-gray-200 dark:border-gray-600">
                <span className="font-black text-gray-900 dark:text-white">{tr.totalNow}</span>
                <span className="font-black text-lg text-cyan-600 dark:text-cyan-400">{subtotal.toFixed(2)} {tr.currency}</span>
              </div>
              {couponData && (
                <p className="text-[11px] text-gray-400 dark:text-gray-500 text-center">{tr.totalNote}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Category Tabs */}
      {categories.length > 0 && (
        <div>
          <div className="flex flex-nowrap gap-2 overflow-x-auto pb-2 scrollbar-hide" style={{ WebkitOverflowScrolling: "touch" }}>
            {categories.map(cat => (
              <button key={cat.id} type="button" onClick={() => setActiveCat(cat.id)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-bold transition-all ${
                  activeCat === cat.id
                    ? `bg-gradient-to-r ${cat.color || "from-cyan-400 to-blue-500"} text-white shadow-md`
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                }`}>
                <span>{cat.icon || "🧺"}</span>
                <span>{isAr ? cat.name : (cat.name_en || cat.name)}</span>
              </button>
            ))}
          </div>

          {/* Services in active category */}
          <div className="mt-3 space-y-2">
            {catServices.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-sm">{tr.noServices}</div>
            ) : (
              catServices.map(renderService)
            )}
          </div>
        </div>
      )}

      {/* Uncategorized services */}
      {uncategorized.length > 0 && (
        <div className="space-y-2">
          {categories.length === 0 && <p className="text-xs font-bold text-muted-foreground">{tr.noCategoryServices}</p>}
          {categories.length > 0 && <p className="text-xs font-bold text-muted-foreground">{tr.otherServices}</p>}
          {uncategorized.map(renderService)}
        </div>
      )}

      {activeServices.length === 0 && (
        <div className="text-center py-6 text-gray-400 text-sm">{tr.noCategoryServices}</div>
      )}
    </div>
  );
}